import type { CancelToken } from 'axios';
import axios from 'axios';
import {
  type QueryFunctionContext,
  type QueryKey,
} from '@tanstack/react-query';

import useQuery, { UseQueryOptionsWithCallbacks } from './react-query/useQuery';

type DebounceQueryOptions<
  TData,
  TQueryKey extends QueryKey,
  TError = unknown,
> = Omit<
  UseQueryOptionsWithCallbacks<TData, TError, TData, TQueryKey>,
  'queryFn'
> & {
  debounceTime?: number;
  onStart?: () => void;
  queryFn: (context: QueryFunctionContext<TQueryKey>) => Promise<TData> | TData;
};

function useSuperQuery<
  TData = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TError = unknown,
>(options: DebounceQueryOptions<TData, TQueryKey, TError>) {
  return useQuery({
    ...options,
    queryFn: (context) => {
      options.onStart?.();

      let cancelId: ReturnType<typeof setTimeout> | undefined;

      const promise = new Promise<TData>((resolve, reject) => {
        const run = () => {
          Promise.resolve(options.queryFn(context)).then(resolve).catch(reject);
        };
        if (options.debounceTime == null) return run();
        cancelId = setTimeout(run, options.debounceTime);
      });

      context.signal.addEventListener('abort', () => {
        if (cancelId) clearTimeout(cancelId);
      });

      return promise;
    },
  });
}

export default useSuperQuery;
